import "./style.scss";

import BeneficiaryFormContainer from "components/BeneficiaryFormContainer";
import NewForm from "components/NewForm";
import DeleteModal from "components/shared/DeleteModal";
import ModalBase from "components/shared/ModalBase";
import { useFormatChannels } from "containers/Channels/utils";
import PublicationFormContainer from "containers/PublicationFormContainer";
import ServicesForm from "containers/ServicesPage/ServicesForm";
import type { IFormStep } from "containers/TermFormContainer";
import { PortalType } from "contexts/portal.context";
import { resolveArticleCalculationBase } from "helpers/pressCalculationBase";
import { getCleanStartorEndDay } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { usePermissions } from "hooks/usePermissions";
import useUserRole from "hooks/useUserRole";
import React, { useContext, useEffect, useMemo, useState } from "react";
import type { Control, FieldErrors, UseFormSetValue, UseFormWatch } from "react-hook-form";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import type { IArticle, ManagementArticle } from "store/articleStore";
import {
    fetchCreateNewArticle,
    fetchDeleteArticle,
    fetchUpdateArticleFromBeneficiary,
    fetchUpdateArticleFromPublication,
} from "store/articleStore";
import { fetchBeneficiariesAutocomplete } from "store/beneficiaryStore";
import type { IService } from "store/servicesStore";
import { getServices } from "store/servicesStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { PressTemplateConfiguration, TermTemplate } from "store/termTemplatesStore";
import { fetchAllTermTemplates, fetchBeneficiaryFrameworkContracts } from "store/termTemplatesStore";
import { fetchTitlesAutocomplete, updatePublicationCount } from "store/titleStore";
import type { TFetchStatus } from "store/types";
import { EPortalType } from "store/types";

import NewArticleStep from "./NewArticleStep";
import PaymentStep from "./PaymentStep";
import type { CompletionStatusOption } from "./utils";
import { completionStatusOptions } from "./utils";

interface IArticleFormContainer {
    mode: "new" | "edit";
    /**
     * Is edit mode active
     */
    editModeActive?: boolean;
    /**
     * Closes edit mode.
     */
    closeEditMode?: () => void;
    closeDrawer?: () => void;
    /**
     * Data of article for edit.
     */
    article?: IArticle | ManagementArticle;
    /**
     * Triggers parent function when isDirty changes (for edits).
     */
    handleDirty?: (isDirty: boolean) => void;
    /**
     * Needed on edit to know what endpoint to choose:
     */
    from?: "publication" | "beneficiary" | "management";
}

export type TArticleData = {
    articleName: string;
    publication: { name: string; id: number } | null;
    beneficiar: { name: string; id: number; surname?: string } | null;
    isEmployee: string;
    folioCount: number | string | undefined;
    folioPrice: number | string | undefined;
    workNature: string | null | undefined | IService;
    channelId: number | undefined | null;
    ircecProfile: IService | string;
    completionStatus: CompletionStatusOption;
    completionDate?: Date | string | null;
};

export type TArticleFormData = {
    control: Control<TArticleData, any>;
    errors: FieldErrors<TArticleData>;
    watch: UseFormWatch<TArticleData>;
    setValue?: UseFormSetValue<TArticleData>;
};

const ArticleFormContainer = ({
    mode,
    editModeActive,
    closeEditMode,
    closeDrawer,
    article,
    handleDirty,
    from,
}: IArticleFormContainer) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const portalType = useContext(PortalType) as "press" | "audiovisual";

    const { hasPermissions } = usePermissions();

    const currentPublicationId = location.state?.publicationId;
    const currentPublicationName = location.state?.publicationName;
    const currentBeneficiaryId = location.state?.beneficiaryId;
    const currentBeneficiaryName = location.state?.beneficiaryFirstName ?? location.state?.beneficiaryName;
    const currentBeneficiarySurname = location.state?.beneficiarySurname;
    const isNewArticleFromManagement = location.state?.from === "management";

    const { services, servicesStatus } = useAppSelector((state) => state.services);
    const { channels } = useAppSelector((state) => state.channels);
    const { termTemplates, beneficiaryFrameworkContracts } = useAppSelector((state) => state.termTemplates);

    const [modalType, setModalType] = useState("");
    const [submitFormStatus, setSubmitFormStatus] = useState<TFetchStatus>("idle");
    const [formType, setFormType] = useState("");

    // Calculation methods:
    const { getCompanySettings, isCompany } = useCompanySettings();
    const usesBothCalculationMethods = getCompanySettings("press_uses_multiple_channels");
    const preferredChannelId = getCompanySettings("press_preferred_channel");
    const usesMinimumGuaranteed = getCompanySettings("uses_minimum_guaranteed_payment");
    const cleanedChannels = useFormatChannels(channels, "press");
    const { userRole } = useUserRole();

    const preferredChannel = cleanedChannels.find(
        (channel) => channel.id === (editModeActive ? article?.channelId : +preferredChannelId),
    );
    const articleCalculationBase = resolveArticleCalculationBase({
        calculationBase: article?.calculationBase,
        channel: preferredChannel,
    });
    const isFolioBased = articleCalculationBase === "folio";
    const [usesFolio, setUsesFolio] = useState(isFolioBased);
    const usesIRCEC = getCompanySettings("uses_ircec");

    const [chosenPaymentMethod, setChosenPaymentMethod] = useState(preferredChannel);

    const hasPaymentMethodChanged = JSON.stringify(chosenPaymentMethod) !== JSON.stringify(preferredChannel);

    const defaultValues: TArticleData = useMemo(() => {
        const publicationEdit = article ? { name: article?.titleName ?? "", id: article?.titleId } : null;
        const publicationNew = currentPublicationId ? { name: currentPublicationName, id: currentPublicationId } : null;

        const beneficiaryEdit = article ? { name: article?.beneficiary ?? "", id: article?.beneficiaryId } : null;
        const beneficiaryNew = currentBeneficiaryId
            ? { name: currentBeneficiaryName ?? "", id: currentBeneficiaryId, surname: currentBeneficiarySurname ?? "" }
            : null;
        /**
         * Amount mode stores the lump sum in packageQuantity; folio mode uses packageQuantity as the count.
         */
        const folioCount = !isFolioBased ? "1" : article?.packageQuantity.toString();

        const folioPrice = (((isFolioBased ? article?.packagePrice : article?.packageQuantity) ?? 0) / 100).toString();

        const articleIrcecOption = services.find((s) => s.id === article?.ircecProfileId) ?? "";
        const defaultCompletedStatus = completionStatusOptions()[1]; // "pending" (non-réalisé)
        const articleCompletionStatusOption =
            completionStatusOptions().find((opt) => opt.value === article?.completionStatus) ?? defaultCompletedStatus;

        return {
            articleName: editModeActive ? article?.name ?? "" : "",
            publication: editModeActive ? publicationEdit : publicationNew,
            beneficiar: editModeActive ? beneficiaryEdit : beneficiaryNew,
            isEmployee: "0",
            folioCount: editModeActive ? folioCount : "",
            folioPrice: editModeActive ? folioPrice : "",
            workNature: editModeActive ? article?.workNature ?? "" : "",
            channelId: editModeActive ? article?.channelId : preferredChannelId,
            ircecProfile: editModeActive ? articleIrcecOption ?? "" : "",
            completionStatus: editModeActive ? articleCompletionStatusOption : defaultCompletedStatus,
            // Rebuild a local Date from the UTC calendar parts so the picker shows the
            // day the user picked, not the previous day when they sit west of UTC.
            completionDate:
                editModeActive && article?.completionDate ? getCleanStartorEndDay(article.completionDate) : null,
        };
    }, [article, services]);

    useEffect(() => {
        if (usesIRCEC && servicesStatus === "idle") {
            dispatch(getServices());
        }
    }, [usesIRCEC, servicesStatus]);

    const {
        trigger,
        handleSubmit,
        reset,
        formState: { isDirty, errors },
        control,
        watch,
        setValue,
    } = useForm<TArticleData>({ defaultValues, mode: "all" });

    const currentPublication = watch("publication");
    const currentBeneficiary = watch("beneficiar");

    useEffect(() => {
        reset(defaultValues);
    }, [services]);

    // Fetch typologies for prefill (generic + framework contracts of the current beneficiary).
    useEffect(() => {
        if (mode !== "new") return;
        dispatch(fetchAllTermTemplates({ beneficiaryId: null }));
    }, [mode]);

    useEffect(() => {
        if (mode !== "new") return;
        const beneficiaryId = currentBeneficiary?.id ?? currentBeneficiaryId;
        if (beneficiaryId && beneficiaryId > 0) {
            dispatch(fetchBeneficiaryFrameworkContracts({ beneficiaryId }));
        }
    }, [currentBeneficiary?.id, currentBeneficiaryId, mode]);

    const injectArticleTemplateData = (template: TermTemplate) => {
        const config = template.configuration as unknown as PressTemplateConfiguration;
        if (typeof config?.folioPrice === "number") {
            setValue("folioPrice", (config.folioPrice / 100).toString(), { shouldDirty: true });
        }
        if (typeof config?.workNature === "string" && config.workNature.length > 0) {
            setValue("workNature", config.workNature, { shouldDirty: true });
        }
    };

    // Opens forms if they choose to create a publication/article.
    useEffect(() => {
        if (currentPublication?.id == -1) {
            setFormType("publication");
            setValue!("publication", null);
        }

        if (currentBeneficiary?.id == -1) {
            setFormType("beneficiary");
            setValue!("beneficiar", null);
        }
    }, [currentPublication, currentBeneficiary]);

    /**
     * Selects correct option of autocomplete once they created a publication/beneficiary.
     * For the option to be complete, we fetch all titles/beneficiaries again.
     * @param data Information needed for the field.
     */
    const handleFormClose = async (type: "publication" | "beneficiar", data: { name: string; id: number }) => {
        if (!data) return;

        const options = { limit: 10, sortOrder: "desc" };
        const response =
            type === "publication"
                ? await dispatch(fetchTitlesAutocomplete(options))
                : await dispatch(fetchBeneficiariesAutocomplete(options));

        if (response.meta.requestStatus === "fulfilled") {
            setFormType("");
            setValue!(type, data, { shouldValidate: true });
        }
    };

    const onSubmit = async (formData) => {
        setSubmitFormStatus("pending");
        const data: any = {};
        data.name = formData.articleName?.trim() ?? "";
        data.titleId = formData.publication.id ?? null;
        data.beneficiaryId = formData.beneficiar.id ?? null;
        data.workNature = formData.workNature ?? null;
        data.completionStatus = formData.completionStatus.value;
        if (formData.completionDate) {
            // Send UTC midnight of the local calendar day so the backend stores the
            // intended day regardless of the user's timezone.
            const local = new Date(formData.completionDate);
            data.completionDate = new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate()));
        } else {
            data.completionDate = null;
        }
        if (usesFolio) {
            data.packagePrice = Math.round(+(formData?.folioPrice ?? 0).toString().replace(",", ".") * 100);
            data.packageQuantity = formData.folioCount ?? 0;
        } else if (!usesFolio) {
            data.packageQuantity = Math.round(+(formData?.folioPrice ?? 0).toString().replace(",", ".") * 100);
        }
        data.calculationBase = usesFolio ? "folio" : "amount";
        data.channelId = chosenPaymentMethod?.id;

        if (usesIRCEC) {
            data.ircecProfileId = formData.ircecProfile.id;
        }

        if (mode === "new") {
            const response = await dispatch(fetchCreateNewArticle({ data, portalType }));
            if (response.meta.requestStatus === "fulfilled") {
                const newArticleId = response.payload.data.id;
                if (isNewArticleFromManagement) {
                    navigate("/management", { state: { newArticleId, promotionFlow: true } });
                } else if (currentPublicationId != undefined)
                    navigate(`/publications/${(data.titleId || currentPublicationId) ?? ""}`, {
                        state: { articleId: newArticleId, promotionFlow: true },
                    });
                else if (currentBeneficiaryId != undefined)
                    navigate(`/beneficiars/${(data.beneficiaryId || currentBeneficiaryId) ?? ""}`, {
                        state: {
                            tab: "2",
                            articleId: newArticleId,
                            promotionFlow: true,
                        },
                    });
                else
                    navigate(`/publications/${data.titleId ?? ""}`, {
                        state: { articleId: newArticleId, promotionFlow: true },
                    });
                setSubmitFormStatus("succeeded");
            } else {
                setSubmitFormStatus("failed");
            }
        }

        if (mode === "edit") {
            if (!article) return;
            if (["management", "publication"].includes(from ?? "")) {
                const response = await dispatch(
                    fetchUpdateArticleFromPublication({
                        data,
                        titleId: article?.titleId,
                        articleId: article?.id,
                        from: from as "publication" | "management",
                    }),
                );
                if (response.meta.requestStatus === "fulfilled") {
                    closeEditMode && closeEditMode();
                    setSubmitFormStatus("succeeded");
                } else {
                    setSubmitFormStatus("failed");
                }
            } else if (from == "beneficiary") {
                const response = await dispatch(
                    fetchUpdateArticleFromBeneficiary({
                        data,
                        beneficiaryId: article.beneficiaryId,
                        articleId: article.id,
                    }),
                );
                if (response.meta.requestStatus === "fulfilled") {
                    closeEditMode && closeEditMode();
                    setSubmitFormStatus("succeeded");
                } else {
                    setSubmitFormStatus("failed");
                }
            }
        }
    };

    const handleDeleteArticle = async () => {
        if (!article) return;
        await dispatch(fetchDeleteArticle({ titleId: article.titleId, articleId: article.id }));
        await dispatch(updatePublicationCount(article.titleId));
        closeEditMode && closeEditMode();
        closeDrawer && closeDrawer();
    };

    const articleSteps: IFormStep[] = [
        {
            stepTitle:
                mode === "new"
                    ? t(`NewArticle.NewArticle_${portalType}`)
                    : `${t(`Articles.EditArticle_${portalType}`)}`,
            stepSubtitle: t(`NewArticle.NewArticleSubtitle_${portalType}`),
            format: (stepTitle: string, stepSubtitle: string) => (
                <NewArticleStep
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={0}
                    useFormData={{ control, errors, watch, setValue }}
                    usesIRCEC={usesIRCEC}
                    services={services}
                    mode={mode}
                    userRole={userRole}
                    isCompany={isCompany}
                    templates={termTemplates}
                    frameworkContracts={beneficiaryFrameworkContracts}
                    onApplyTypology={injectArticleTemplateData}
                />
            ),
        },
        {
            stepTitle:
                usesBothCalculationMethods || !usesFolio
                    ? portalType === "press"
                        ? t("NewArticle.PaymentAndOptions")
                        : t("NewArticle.ForfaitStep")
                    : t("NewArticle.FolioAndOption"),
            stepSubtitle: t("NewArticle.FolioAndOptionSubtitle"),
            format: (stepTitle: string, stepSubtitle: string) => (
                <PaymentStep
                    stepTitle={stepTitle}
                    stepSubtitle={stepSubtitle}
                    indexStep={1}
                    useFormData={{ control, errors, watch }}
                    setUsesFolio={setUsesFolio}
                    calculationMethods={{ usesFolio, usesBothCalculationMethods, usesMinimumGuaranteed }}
                    paymentMethod={{ chosenPaymentMethod, setChosenPaymentMethod }}
                    mode={mode}
                    cleanedChannels={cleanedChannels}
                />
            ),
        },
    ];

    useEffect(() => {
        handleDirty && handleDirty(isDirty || hasPaymentMethodChanged);
    }, [isDirty, hasPaymentMethodChanged]);

    const ircecProfile = watch("ircecProfile");

    useEffect(() => {
        if (usesIRCEC && (ircecProfile as IService)?.id === -1) {
            setModalType("ircecProfile");
            setValue!("ircecProfile", {} as IService);
        }
    }, [usesIRCEC, services, ircecProfile]);

    const handleCloseIrcecProfileModal = (service: IService | undefined) => {
        if (service) {
            setValue!("ircecProfile", service);
            setModalType("");
        }
    };

    const deleteStep = {
        title: t(`Articles.DeleteArticle_${portalType}`),
        subtitle: t(`Articles.DeleteText_${portalType}`),
    };

    const deleteConfirmationDescription = (
        <>
            <p>{t(`Articles.DeleteText_${portalType}`)}</p>
        </>
    );

    return (
        <>
            <div
                className="article-form-container"
                style={{ display: ["publication", "beneficiary"].includes(formType) ? "none" : "" }}
            >
                <NewForm
                    steps={articleSteps}
                    dataForSteps={[]}
                    mode={mode}
                    editModeActive={editModeActive}
                    closeEditMode={closeEditMode}
                    txtBtnSubmitNew={t(`NewArticle.CreateArticle_${portalType}`)}
                    onSubmit={onSubmit}
                    onDelete={() => setModalType("delete")}
                    useFormData={{ trigger, handleSubmit, isDirty }}
                    submitFormStatus={submitFormStatus}
                    type="article"
                    haveInDepthValuesChanged={hasPaymentMethodChanged}
                    deleteStep={hasPermissions(["article:new"]) ? deleteStep : undefined}
                    infoLink={{
                        type: t(`Articles.Article_${portalType}`, { count: 1 }).toLowerCase(),
                        link: "https://presse-helpdesk.crealo.app/fr/article/creer-un-article-1ki29vc/",
                        context: portalType === EPortalType.PRESS ? "male" : "female",
                    }}
                />
                <DeleteModal
                    elementTitle={`${article?.name ?? ""}`}
                    open={modalType === "delete" && hasPermissions(["article:new"])}
                    onClose={() => setModalType("")}
                    onDelete={handleDeleteArticle}
                    needsDoubleQuotes
                    description={deleteConfirmationDescription}
                    needsConfirmation
                    confirmationText={t(`Articles.Delete_${portalType}`).toUpperCase()}
                />

                <ModalBase
                    open={modalType === "ircecProfile"}
                    onClose={() => {
                        setModalType(""), setValue!("ircecProfile", {} as IService);
                    }}
                    modalTitle={t("Services.CreateService")}
                    backgroundColor="#65656580"
                >
                    <ServicesForm type={"create"} handleClose={handleCloseIrcecProfileModal} from="articleForm" />
                </ModalBase>
            </div>
            {formType === "publication" && <PublicationFormContainer handleModalClose={handleFormClose} mode="new" />}
            {formType === "beneficiary" && <BeneficiaryFormContainer mode="new" handleModalClose={handleFormClose} />}
        </>
    );
};

export default React.memo(ArticleFormContainer);
